import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

export type CountryOption = {
  code: string;
  name: string;
};

interface CountrySelectProps {
  id?: string;
  value?: string;
  onChange: (_value: string) => void;
  priorityOptions?: CountryOption[];
  remainingOptions?: CountryOption[];
  placeholder?: string;
  disabled?: boolean;
  ariaInvalid?: boolean;
}

const normalizeFilter = (value: string) => value.trim().toLowerCase();

const filterOptions = (options: CountryOption[], query: string) => {
  if (!query) {
    return options;
  }

  return options.filter((option) => {
    const code = option.code.toLowerCase();
    const name = option.name.toLowerCase();
    return code.includes(query) || name.includes(query);
  });
};

export function CountrySelect({
  id,
  value,
  onChange,
  priorityOptions = [],
  remainingOptions = [],
  placeholder,
  disabled = false,
  ariaInvalid = false,
}: CountrySelectProps) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState('');
  const searchRef = useRef<HTMLInputElement | null>(null);

  useEffect(() => {
    if (!open) {
      setSearch('');
      return;
    }
    const frame = requestAnimationFrame(() => searchRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  const optionsByCode = useMemo(() => {
    const map = new Map<string, CountryOption>();
    [...priorityOptions, ...remainingOptions].forEach((option) => {
      if (!map.has(option.code)) {
        map.set(option.code, option);
      }
    });
    return map;
  }, [priorityOptions, remainingOptions]);

  const normalizedValue = (value ?? '').toUpperCase();
  const selectedLabel = normalizedValue
    ? optionsByCode.get(normalizedValue)?.name ?? normalizedValue
    : undefined;
  const query = normalizeFilter(search);
  const filteredPriority = useMemo(
    () => filterOptions(priorityOptions, query),
    [priorityOptions, query],
  );
  const filteredRemaining = useMemo(
    () => filterOptions(remainingOptions, query),
    [remainingOptions, query],
  );
  const hasResults = filteredPriority.length > 0 || filteredRemaining.length > 0;

  return (
    <Select
      value={normalizedValue}
      onValueChange={(next) => {
        onChange(next.toUpperCase());
      }}
      onOpenChange={setOpen}
      disabled={disabled}
    >
      <SelectTrigger
        id={id}
        aria-invalid={ariaInvalid}
        className={ariaInvalid ? 'border-destructive focus-visible:ring-destructive/30' : undefined}
      >
        <SelectValue placeholder={placeholder}>
          {selectedLabel}
        </SelectValue>
      </SelectTrigger>
      <SelectContent className="w-[var(--radix-select-trigger-width)]">
        <div
          className="sticky top-0 z-10 border-b bg-popover p-1"
          onPointerDown={(event) => event.stopPropagation()}
          onKeyDown={(event) => event.stopPropagation()}
        >
          <Input
            ref={searchRef}
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder={t('checkout.countryDropdown.searchPlaceholder', 'Search country')}
            className="h-8"
          />
        </div>
        {hasResults ? (
          <>
            {filteredPriority.length > 0 ? (
              filteredPriority.map((option) => (
                <SelectItem key={`priority-${option.code}`} value={option.code}>
                  {option.name} ({option.code})
                </SelectItem>
              ))
            ) : null}
            {filteredPriority.length > 0 && filteredRemaining.length > 0 ? (
              <div
                role="separator"
                aria-orientation="horizontal"
                className="mx-1 my-2 h-px bg-border"
              />
            ) : null}
            {filteredRemaining.length > 0 ? (
              filteredRemaining.map((option) => (
                <SelectItem key={`remaining-${option.code}`} value={option.code}>
                  {option.name} ({option.code})
                </SelectItem>
              ))
            ) : null}
          </>
        ) : (
          <div className="px-2 py-3 text-sm text-muted-foreground">
            {t('checkout.countryDropdown.noResults', 'No countries found')}
          </div>
        )}
      </SelectContent>
    </Select>
  );
}
